import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "relative w-full h-full" }
const _hoisted_2 = {
  key: 0,
  class: "absolute top-2 left-2 z-[400] bg-card/90 backdrop-blur rounded-md px-3 py-2 text-xs text-white shadow-lg max-w-[260px]"
}
const _hoisted_3 = { class: "font-semibold truncate" }
const _hoisted_4 = { class: "text-gray-300" }
const _hoisted_5 = { class: "text-gray-400 mt-1" }
const _hoisted_6 = {
  key: 1,
  class: "absolute inset-0 z-[500] flex items-center justify-center bg-black/30 text-white text-sm"
}
const _hoisted_7 = {
  key: 2,
  class: "absolute bottom-3 left-1/2 -translate-x-1/2 z-[500] bg-card/95 text-yellow-300 text-xs rounded-md px-3 py-2 shadow-lg"
}

import { ref, watch, onMounted, onBeforeUnmount } from "vue";
	import L from "leaflet";
	import "leaflet/dist/leaflet.css";
	import api from "@/api";

	
export default /*#__PURE__*/_defineComponent({
  __name: 'EmbarkationTrailMap',
  props: {
    embarkationId: {}
  },
  setup(__props: any, { expose: __expose }) {

	/**
	 * Renders the GPS trail of a single embarkation as a Leaflet polyline,
	 * with a start marker (green flag) and an end marker (red flag).
	 *
	 * Data source : GET /v1/tracking/embarkations/{id}/trail
	 *   → ordered list of { lat, lng, speed, heading, recorded_at } points
	 *     bounded to embarkation_date ± 24h server-side.
	 *
	 * The map fits to the polyline bounds on load, plus exposes a
	 * `refresh()` method so the parent can re-fetch when a different
	 * embarkation is selected without re-mounting the component.
	 */
	const props = __props;

	const mapEl = ref<HTMLElement | null>(null);
	const isLoading = ref(false);
	const errorMsg = ref<string | null>(null);
	const meta = ref<{ label?: string; status?: string; matriculation?: string; pointCount?: number } | null>(null);
	let map: L.Map | null = null;
	let polyline: L.Polyline | null = null;
	const endpointMarkers: L.Marker[] = [];

	const flagIcon = (color: string): L.DivIcon =>
		L.divIcon({
			html: `<div style="width:20px;height:24px;display:flex;align-items:flex-end;justify-content:center">
				<svg viewBox="0 0 24 24" width="20" height="24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
					<path d="M5 21V3h14l-3 5 3 5H7v8H5z" fill="${color}" stroke="#fff" stroke-width="1"/>
				</svg>
			</div>`,
			className: "trail-endpoint",
			iconSize: [20, 24],
			iconAnchor: [4, 24],
		});

	const clearOverlays = () => {
		if (polyline && map) {
			polyline.remove();
			polyline = null;
		}
		endpointMarkers.forEach((m) => m.remove());
		endpointMarkers.length = 0;
	};

	const fetchTrail = async (id: number) => {
		isLoading.value = true;
		errorMsg.value = null;
		try {
			const r: any = await api().vehiclePosition.trail(id);
			const data = r?.data || r;
			const points: Array<{ lat: number; lng: number }> = Array.isArray(data?.points)
				? data.points.filter((p: any) => p.lat != null && p.lng != null)
				: [];
			meta.value = {
				label: data?.embarkation?.label,
				status: data?.embarkation?.status,
				matriculation: data?.vehicle?.matriculation,
				pointCount: points.length,
			};

			clearOverlays();

			if (!map) return;

			if (points.length === 0) {
				errorMsg.value = "Aucun point GPS enregistré pour ce trajet.";
				return;
			}

			const latlngs: L.LatLngExpression[] = points.map((p) => [p.lat, p.lng]);
			polyline = L.polyline(latlngs, {
				color: "#16a34a",
				weight: 4,
				opacity: 0.9,
				lineJoin: "round",
			}).addTo(map);

			const start = L.marker(latlngs[0] as L.LatLngExpression, { icon: flagIcon("#16a34a") })
				.bindTooltip("Départ", { direction: "top", offset: [0, -20] })
				.addTo(map);
			const end = L.marker(latlngs[latlngs.length - 1] as L.LatLngExpression, { icon: flagIcon("#dc2626") })
				.bindTooltip("Arrivée", { direction: "top", offset: [0, -20] })
				.addTo(map);
			endpointMarkers.push(start, end);

			map.fitBounds(polyline.getBounds().pad(0.15));
		} catch (e: any) {
			console.warn("[trail] fetch failed", e);
			errorMsg.value = "Impossible de charger le trajet.";
			clearOverlays();
		} finally {
			isLoading.value = false;
		}
	};

	watch(
		() => props.embarkationId,
		(id) => {
			if (id != null) fetchTrail(id);
			else {
				clearOverlays();
				meta.value = null;
				errorMsg.value = null;
			}
		}
	);

	onMounted(async () => {
		if (!mapEl.value) return;
		map = L.map(mapEl.value, { zoomControl: true, attributionControl: true })
			.setView([4.05, 11.51], 6);
		L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
			maxZoom: 19,
			attribution: "&copy; OpenStreetMap",
		}).addTo(map);
		if (props.embarkationId != null) await fetchTrail(props.embarkationId);
	});

	onBeforeUnmount(() => {
		clearOverlays();
		if (map) {
			map.remove();
			map = null;
		}
	});

	__expose({ refresh: () => props.embarkationId != null && fetchTrail(props.embarkationId) });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "mapEl",
      ref: mapEl,
      class: "w-full h-full min-h-[400px] rounded bg-gray-900"
    }, null, 512),
    (meta.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(meta.value.label || "Embarquement"), 1),
          _createElementVNode("div", _hoisted_4, _toDisplayString(meta.value.matriculation || "—") + " · " + _toDisplayString(meta.value.status || "—"), 1),
          _createElementVNode("div", _hoisted_5, _toDisplayString(meta.value.pointCount) + " point(s) GPS", 1)
        ]))
      : _createCommentVNode("", true),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Chargement du trajet… "))
      : _createCommentVNode("", true),
    (errorMsg.value && !isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(errorMsg.value), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})